import { useMemo, useState } from 'react';
import { Bell, Menu, RefreshCw, Search, UserRound } from 'lucide-react';
import { formatInr } from '../utils/market.js';

function MiniSparkline({ positive, index }) {
  const paths = [
    'M1 24 L10 20 L18 21 L28 12 L38 15 L48 6 L59 9 L70 3',
    'M1 20 L10 23 L20 15 L30 18 L40 10 L51 12 L60 5 L70 8',
    'M1 7 L12 10 L23 8 L34 17 L45 14 L57 23 L70 20',
    'M1 22 L9 18 L20 20 L30 12 L41 14 L54 5 L70 2',
  ];
  return (
    <svg viewBox="0 0 72 28" aria-hidden="true" className="index-sparkline">
      <path d={paths[index % paths.length]} fill="none" stroke={positive ? '#39d296' : '#f1737e'} strokeWidth="2" />
    </svg>
  );
}

export default function Header({
  indices,
  dataMeta,
  marketStatus,
  onRefresh,
  refreshing,
  onSelectStock,
  stocks,
  onToggleMenu,
  onNavigate,
}) {
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];
    return stocks
      .filter((item) => `${item.symbol} ${item.name} ${item.sector}`.toLowerCase().includes(needle))
      .sort((first, second) => {
        const firstExact = first.symbol.toLowerCase().startsWith(needle) ? 0 : 1;
        const secondExact = second.symbol.toLowerCase().startsWith(needle) ? 0 : 1;
        return firstExact - secondExact || first.symbol.localeCompare(second.symbol);
      })
      .slice(0, 6);
  }, [query, stocks]);

  const chooseStock = (stock) => {
    onSelectStock(stock.symbol);
    setQuery('');
    setSearchOpen(false);
  };

  const handleSearchKey = (event) => {
    if (event.key === 'Escape') {
      setSearchOpen(false);
      return;
    }
    if (event.key === 'Enter' && matches[0]) chooseStock(matches[0]);
  };

  return (
    <>
      <header className="topbar">
        <div className="brand-wrap">
          <button className="icon-button menu-button" onClick={onToggleMenu} aria-label="Open navigation">
            <Menu size={20} />
          </button>
          <div className="brand-mark" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <div>
            <div className="brand-name">VIZMARKETS</div>
            <div className="brand-subtitle">Indian market intelligence</div>
          </div>
        </div>

        <div className="global-search" onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setSearchOpen(false); }}>
          <Search size={17} />
          <input
            aria-label="Search stocks"
            role="combobox"
            aria-autocomplete="list"
            aria-controls="global-search-results"
            aria-expanded={searchOpen && Boolean(query)}
            placeholder="Search symbol or company"
            value={query}
            onChange={(event) => { setQuery(event.target.value); setSearchOpen(true); }}
            onFocus={() => setSearchOpen(true)}
            onKeyDown={handleSearchKey}
          />
          <kbd>↵</kbd>
          {searchOpen && query && (
            <div className="global-search-results" id="global-search-results" role="listbox">
              {matches.length ? matches.map((stock) => (
                <button key={stock.symbol} role="option" aria-selected="false" onClick={() => chooseStock(stock)}>
                  <span className="stock-monogram">{stock.symbol.slice(0, 2)}</span>
                  <span><strong>{stock.symbol}</strong><small>{stock.name} · {stock.sector}</small></span>
                  <em>{formatInr(stock.price)}</em>
                </button>
              )) : <div className="search-empty"><strong>No matching stock</strong><span>Try a symbol such as TCS or RELIANCE.</span></div>}
            </div>
          )}
        </div>

        <div className="topbar-actions">
          <div className={`session-pill ${marketStatus.tone}`}>
            <span className="live-dot" />
            <div>
              <strong>{marketStatus.label}</strong>
              <small>{dataMeta.status === 'provider' ? 'Provider feed · may be delayed' : 'Demonstration snapshot'}</small>
            </div>
          </div>
          <button className="icon-button" aria-label="Refresh market data" onClick={onRefresh} disabled={refreshing}>
            <RefreshCw size={18} className={refreshing ? 'spin' : ''} />
          </button>
          <button className="icon-button notification-button" aria-label="Open watchlist and alerts" onClick={() => onNavigate('watchlist')}>
            <Bell size={18} />
            <span aria-hidden="true" />
          </button>
          <div className="profile-button" aria-label="Guest session">
            <span className="avatar"><UserRound size={16} /></span>
            <span>Guest</span>
          </div>
        </div>
      </header>

      <section className="index-strip" aria-label="Market indices">
        {indices.map((item, index) => (
          <article className="index-item" key={item.symbol}>
            <div>
              <span className="index-label">{item.name}</span>
              <span className="venue">{item.venue}</span>
            </div>
            <strong>{formatInr(item.value).replace('₹', '')}</strong>
            <span className={item.change >= 0 ? 'positive' : 'negative'}>
              {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)}%
            </span>
            <MiniSparkline positive={item.change >= 0} index={index} />
          </article>
        ))}
        <div className="strip-meta">
          <span>AS OF</span>
          <strong>{new Date(dataMeta.asOf).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' })}</strong>
          <small>IST label · source timestamp</small>
        </div>
      </section>
    </>
  );
}
